import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Database, Loader2, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type AdminTenant, getAdminTenants, PlatformApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/tenants/')({
  component: PlatformTenantsPage,
})

function PlatformTenantsPage() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; tenants: AdminTenant[] }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  useEffect(() => {
    // No didRun guard here — in React StrictMode the effect mounts
    // twice. The abort pattern handles that cleanly: cleanup aborts
    // the first fetch (catch returns early on AbortError), the
    // second mount starts a fresh fetch that completes. A
    // didRun-ref + abort combo would leave us stuck in loading
    // because the guarded second run would skip the new fetch.
    const ctrl = new AbortController()
    getAdminTenants(ctrl.signal)
      .then((tenants) => setState({ status: 'loaded', tenants }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load tenants'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [])

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Tenants</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every workspace on the platform.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {state.status === 'loaded' ? (
            <div className="text-xs text-muted-foreground">
              {state.tenants.length} {state.tenants.length === 1 ? 'tenant' : 'tenants'}
            </div>
          ) : null}
          <Link
            to="/platform/tenants/new"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium',
              'bg-primary text-primary-foreground shadow-md shadow-primary/20',
              'hover:opacity-90 transition-opacity',
            )}
          >
            <Plus className="h-4 w-4" />
            New tenant
          </Link>
        </div>
      </header>

      {state.status === 'loading' ? <LoadingState /> : null}
      {state.status === 'error' ? <ErrorState message={state.message} /> : null}
      {state.status === 'loaded' && state.tenants.length === 0 ? <EmptyState /> : null}
      {state.status === 'loaded' && state.tenants.length > 0 ? (
        <TenantTable tenants={state.tenants} />
      ) : null}
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading tenants…
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  )
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center"
    >
      <div
        className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)',
          filter: 'blur(36px)',
        }}
      />
      <div className="relative">
        <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
          <Database className="h-5 w-5" />
        </div>
        <h3 className="mt-4 text-lg font-semibold">No tenants yet</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          The platform has no tenants. Once someone signs up and gets a workspace provisioned,
          they'll appear here.
        </p>
      </div>
    </motion.div>
  )
}

function TenantTable({ tenants }: { tenants: AdminTenant[] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Tier</Th>
              <Th>Status</Th>
              <Th>Owners</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tenants.map((tenant) => (
              <tr key={tenant.id} className="transition-colors hover:bg-accent/40">
                <Td>
                  <Link
                    to="/platform/tenants/$tenantId"
                    params={{ tenantId: tenant.id }}
                    className="group block"
                  >
                    <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                      {tenant.name}
                    </div>
                    {tenant.emailDomain ? (
                      <div className="text-xs text-muted-foreground">{tenant.emailDomain}</div>
                    ) : null}
                  </Link>
                </Td>
                <Td>
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenant.slug}</code>
                </Td>
                <Td>
                  <TierBadge tier={tenant.tier} />
                </Td>
                <Td>
                  <StatusBadge status={tenant.status} />
                </Td>
                <Td>
                  {tenant.members.length === 0 ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {tenant.members.map((m) => (
                        <span key={m.userId} className="text-xs">
                          <span className="font-medium">{m.email}</span>
                          <span className="text-muted-foreground"> · {m.role}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </Td>
                <Td>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(tenant.createdAt)}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-2.5 text-left font-semibold">
      {children}
    </th>
  )
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>
}

function TierBadge({ tier }: { tier: string }) {
  const label = tier === 'business' ? 'Business' : tier === 'enterprise' ? 'Enterprise' : 'Personal'
  const tone =
    tier === 'enterprise'
      ? 'bg-primary/10 text-primary ring-primary/25'
      : tier === 'business'
        ? 'bg-chart-3/10 text-chart-3 ring-chart-3/25'
        : 'bg-muted text-muted-foreground ring-border'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-lg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function StatusBadge({ status }: { status: string | null }) {
  const label = status ?? '—'
  const tone =
    status === 'active'
      ? 'bg-success/10 text-success ring-success/25'
      : status === 'paused'
        ? 'bg-warning/10 text-warning ring-warning/25'
        : status === 'suspended' || status === 'deleted'
          ? 'bg-destructive/10 text-destructive ring-destructive/25'
          : 'bg-muted text-muted-foreground ring-border'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-lg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
